import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Wallet, AlertTriangle, Play, CheckCircle2, Link2, Clock, ChevronDown, ChevronRight, FolderClosed } from "lucide-react";
import { api } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function PayrollCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({}); // {founder_id: {split_pct, payout_bank_id}}
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [running, setRunning] = useState(false);
  const [connect, setConnect] = useState({}); // founder_id -> status
  const [connecting, setConnecting] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadHistory = async () => {
    try { const { data } = await api.get("/admin/payroll/history"); setHistory(data); }
    catch { /* non-critical */ }
  };

  const loadConnect = async () => {
    try {
      const { data } = await api.get("/admin-auth/connect/status");
      const map = {};
      (data.founders || []).forEach((f) => { map[f.founder_id] = f; });
      setConnect(map);
    } catch { /* non-critical — founder may be break-glass admin */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payroll");
      setData(data);
      const d = {};
      (data.founders || []).forEach((f) => { d[f.founder_id] = { split_pct: f.split_pct, payout_bank_id: f.payout_bank_id || "" }; });
      setDraft(d);
    } catch { toast.error("Could not load payroll."); }
    loadHistory();
    loadConnect();
  };
  useEffect(() => { load(); }, []);

  // Handle Stripe Connect onboarding redirect back to /admin
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connect_return") === "1") {
      toast.success("Payout account details saved — verifying with Stripe…");
      loadConnect();
    } else if (p.get("connect_refresh") === "1") {
      toast.message("Onboarding link expired — click Connect again to resume.");
    }
    if (p.get("connect_return") || p.get("connect_refresh")) {
      p.delete("connect_return"); p.delete("connect_refresh"); p.delete("founder_id");
      const qs = p.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const startOnboard = async (founderId) => {
    setConnecting(founderId);
    try {
      const { data } = await api.post("/admin-auth/connect/onboard", { founder_id: founderId });
      window.location.href = data.url; // Stripe-hosted onboarding
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payout onboarding.");
      setConnecting(null);
    }
  };

  const setField = (fid, key, val) => setDraft((d) => ({ ...d, [fid]: { ...d[fid], [key]: val } }));

  const totalPct = Object.values(draft).reduce((s, c) => s + (Number(c.split_pct) || 0), 0);
  const revenue = data?.revenue || 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/admin/payroll", { payroll: draft });
      setData(fresh);
      toast.success("Payroll saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save payroll."); }
    finally { setSaving(false); }
  };

  const runPayroll = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/payroll/run", {});
      const paid = data.paid_out || 0;
      toast.success(
        paid > 0
          ? `Pay run · ${money(paid)} sent via Stripe of ${money(data.allocated)} across ${data.splits.length} founder${data.splits.length === 1 ? "" : "s"}.`
          : `Pay run logged · ${money(data.allocated)} across ${data.splits.length} founder${data.splits.length === 1 ? "" : "s"} (connect a payout account to auto-send).`
      );
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log pay run."); }
    finally { setRunning(false); }
  };

  if (!data) return <div className="py-8 flex items-center justify-center gap-2 text-[#1e3d3a]/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading payroll…</div>;

  return (
    <div data-testid="payroll-card" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[#c9a961]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Payroll · revenue split</span>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Collected revenue</p>
          <p className="font-head text-xl text-[#1e3d3a]" data-testid="payroll-revenue">{money(revenue)}</p>
        </div>
      </div>
      <p className="mt-1 text-sm text-[#1e3d3a]/65 max-w-2xl">
        Set each founder's share of collected revenue and the bank account they get paid into.
      </p>

      <div className="mt-4 space-y-3">
        {data.founders.map((f) => {
          const c = draft[f.founder_id] || {};
          const payout = revenue * (Number(c.split_pct) || 0) / 100;
          const cs = connect[f.founder_id] || {};
          const ready = cs.payouts_enabled;
          const started = cs.connected && !cs.payouts_enabled;
          return (
            <div key={f.founder_id} data-testid={`payroll-row-${f.username}`} className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 p-3">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
              <div className="sm:col-span-3">
                <p className="font-medium text-[#1e3d3a]">{f.display_name}</p>
                <p className="text-[11px] text-[#1e3d3a]/45">@{f.username}</p>
              </div>
              <div className="sm:col-span-3">
                <label className="block text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 mb-1">Revenue split %</label>
                <input
                  type="number" min="0" max="100"
                  data-testid={`payroll-split-${f.username}`}
                  value={c.split_pct ?? 0}
                  onChange={(e) => setField(f.founder_id, "split_pct", e.target.value)}
                  className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none"
                />
              </div>
              <div className="sm:col-span-4">
                <label className="block text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 mb-1">Payout bank</label>
                <select
                  data-testid={`payroll-bank-${f.username}`}
                  value={c.payout_bank_id || ""}
                  onChange={(e) => setField(f.founder_id, "payout_bank_id", e.target.value)}
                  className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none"
                >
                  <option value="">{f.banks.length ? "— select bank —" : "No bank on file yet"}</option>
                  {f.banks.map((b) => (
                    <option key={b.id} value={b.id}>{b.bank_name}{b.last4 ? ` ····${b.last4}` : ""}{b.is_primary ? " (primary)" : ""}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 text-right">
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Gets paid</p>
                <p className="font-head text-lg text-[#4d5a24]" data-testid={`payroll-payout-${f.username}`}>{money(payout)}</p>
              </div>
              </div>

              {/* Stripe Connect payout account status */}
              <div className="mt-2.5 pt-2.5 border-t border-[#1e3d3a]/10 flex items-center justify-between gap-3">
                {ready ? (
                  <span data-testid={`connect-status-${f.username}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#4d5a24]">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Stripe payout account ready · splits auto-transfer
                  </span>
                ) : started ? (
                  <span data-testid={`connect-status-${f.username}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#a3823f]">
                    <Clock className="h-3.5 w-3.5" /> Onboarding started — finish verification to enable payouts
                  </span>
                ) : (
                  <span data-testid={`connect-status-${f.username}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#1e3d3a]/55">
                    <AlertTriangle className="h-3.5 w-3.5" /> No Stripe payout account — splits log only until connected
                  </span>
                )}
                {!ready && (
                  <button
                    type="button"
                    data-testid={`connect-onboard-${f.username}`}
                    onClick={() => startOnboard(f.founder_id)}
                    disabled={connecting === f.founder_id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#635bff] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-60 transition-opacity duration-300"
                  >
                    {connecting === f.founder_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                    {started ? "Resume setup" : "Connect payout account"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className={`text-xs flex items-center gap-1.5 ${totalPct > 100 ? "text-[#b23a48]" : "text-[#1e3d3a]/55"}`}>
          {totalPct > 100 && <AlertTriangle className="h-3.5 w-3.5" />}
          Total allocated: <span className="font-medium">{totalPct}%</span>{totalPct > 100 ? " · over 100%" : ""}
        </p>
        <button
          type="button"
          data-testid="payroll-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save payroll
        </button>
      </div>

      {/* Payroll history — collapsible folder of auto splits + manual pay runs */}
      <div className="mt-6 border-t border-[#1e3d3a]/10 pt-4" data-testid="payroll-history">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="payroll-history-toggle"
            onClick={() => setHistoryOpen((o) => !o)}
            className="flex items-center gap-2 group"
          >
            {historyOpen
              ? <ChevronDown className="h-4 w-4 text-[#c9a961]" />
              : <ChevronRight className="h-4 w-4 text-[#c9a961]" />}
            <FolderClosed className="h-4 w-4 text-[#c9a961]" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 group-hover:text-[#1e3d3a]">Payroll history</span>
            <span className="text-[11px] text-[#1e3d3a]/45">
              · {history?.events?.length || 0} pay run{(history?.events?.length || 0) === 1 ? "" : "s"}
              {history ? ` · ${money(history.total_distributed)} distributed` : ""}
            </span>
          </button>
          <button
            type="button"
            data-testid="payroll-run"
            onClick={runPayroll}
            disabled={running}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-50 transition-colors duration-300"
          >
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Log a pay run
          </button>
        </div>

        {historyOpen && (
          <div data-testid="payroll-history-body">
            {history?.totals?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {history.totals.map((t, i) => (
                  <span key={i} className="rounded-full bg-[#6b7c3a]/10 text-[#4d5a24] px-3 py-1 text-[11px]">
                    {t.name}: <span className="font-medium">{money(t.total)}</span> paid to date
                  </span>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
              {(!history || history.events.length === 0) && (
                <p className="text-xs text-[#1e3d3a]/45">No pay activity yet. Splits log automatically as client payments land.</p>
              )}
              {history?.events?.map((e) => (
                <div key={e.id} data-testid={`payroll-event-${e.id}`} className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-[#1e3d3a]">
                      {e.type === "auto" ? "Client payment" : "Manual pay run"}
                      <span className="text-[#1e3d3a]/45 font-normal"> · {money(e.payment_amount)}{e.plan ? ` · ${e.plan}` : ""}{e.client_email ? ` · ${e.client_email}` : ""}</span>
                    </span>
                    <span className="text-[10px] text-[#1e3d3a]/40">{e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                    {(e.splits || []).map((s, j) => {
                      const ts = s.transfer_status;
                      const tag = ts === "paid"
                        ? <span className="text-[#4d5a24]" title="Sent via Stripe">✓ sent</span>
                        : ts === "failed"
                        ? <span className="text-[#b23a48]" title={s.transfer_error || "Transfer failed"}>⚠ failed</span>
                        : (ts && ts.startsWith("pending"))
                        ? <span className="text-[#a3823f]" title="Founder hasn't connected a Stripe payout account yet">• logged</span>
                        : null;
                      return (
                        <span key={j} className="text-[11px] text-[#1e3d3a]/65">
                          {s.name} <span className="text-[#4d5a24] font-medium">{money(s.amount)}</span> ({s.pct}%){s.bank_name ? ` → ${s.bank_name}` : ""} {tag}
                        </span>
                      );
                    })}
                    {(e.splits || []).length === 0 && <span className="text-[11px] text-[#b23a48]">No splits set — configure payroll % above.</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
